import { ChangeDetectorRef, NgModule, NgZone, Pipe } from '@angular/core';
import { HeaderComponent } from './containers/header/header.component';
import { FullLayoutComponent } from './components/full-layout/full-layout.component';
import { SharedModule } from '../shared/shared.module';
import { LayoutSwitcherComponent } from './containers/header/layout-switcher/layout-switcher.component';
import { LocationPickerComponent } from './containers/header/location-picker/location-picker.component';
import { UserMenuComponent } from './containers/header/user-menu/user-menu.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { AuthModule } from '../auth/auth.module';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MinimalLayoutComponent } from './components/minimal-layout/minimal-layout.component';
import { ArrowComponent } from './components/arrow/arrow.component';
import { TimeAgoPipe } from 'time-ago-pipe';
import { AttachmentComponent } from './components/attachment/attachment.component';
import { InsightCardComponent } from './components/insight-card/insight-card.component';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { ConsolidationTrendChartComponent } from './components/consolidation-trend-chart/consolidation-trend-chart.component';
import { ConsolidationChartComponent } from './components/consolidation-chart/consolidation-chart.component';
import { CarouselComponent } from './components/carousel/carousel.component';
import { CarouselItemComponent } from './components/carousel-item/carousel-item.component';
import { OnboardingOverlayComponent } from './components/onboarding-overlay/onboarding-overlay.component';
import { FromNowPipe } from './pipes/from-now.pipe';
import { EmailPreviewComponent } from './components/email-preview/email-preview.component';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { ZoomableImageComponent } from './components/zoomable-image/zoomable-image.component';
import { LocationSidebarComponent } from './components/location-sidebar/location-sidebar.component';
import { CustomerHeaderComponent } from './components/customer-header/customer-header.component';
import { LocationGuardComponent } from './containers/location-guard/location-guard.component';
import { LocationsModuleComponent } from './containers/locations-module/locations-module.component';
import { UserModuleComponent } from './containers/user-module/user-module.component';
import { MobileLocationPickerComponent } from './components/mobile-location-picker/mobile-location-picker.component';
import { FeedbackPanelComponent } from './containers/feedback-panel/feedback-panel.component';
import { NegativeFeedbackDialogComponent } from './containers/negative-feedback-dialog/negative-feedback-dialog.component';
import { FeedbackDialogComponent } from './feedback-dialog/feedback-dialog.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { ImageSwapperComponent } from './components/image-swapper/image-swapper.component';
import { CardTitleComponent } from './components/card-title/card-title.component';
import { TimeDurationPipe } from './pipes/time-duration.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { DialogComponent } from './components/dialog/dialog.component';
import { DrawerPanelComponent } from './components/drawer-panel/drawer-panel.component';
import { DrawerComponent } from './components/drawer/drawer.component';
import { DrawerPanelService } from './services/drawer-panel.service';
import { MultilineTranslationComponent } from './components/multiline-translation/multiline-translation.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { BarRatingModule } from 'ngx-bar-rating';
import { EnablePushPanelComponent } from './components/enable-push-panel/enable-push-panel.component';
import { QsrLogoPipe } from './pipes/qsr-logo.pipe';
import { RemoveAriaOwnsDirective } from './directives/remove-ariaOwns.directive';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { PosCategorySopDialogComponent } from './components/pos-category-sop-dialog/pos-category-sop-dialog.component';
import { HasFeaturePipe } from './pipes/has-feature';
import { VerticalBarChartWithAverageComponent } from './components/vertical-bar-chart-with-average/vertical-bar-chart-with-average.component';
import { CustomCurrencyPipe } from "../../store/pipes/custom-currency.pipe";
import { HasTimelinessPipe } from './pipes/has-timeliness';
import { HasMatchingPipe } from './pipes/has-matching';
import { CustomerFacingCategoryLabelPipe } from './pipes/customer-facing-category-label.pipe';

const sharedMatModules = [
  MatToolbarModule,
  MatSelectModule,
  MatMenuModule,
  MatButtonToggleModule,
  MatProgressSpinnerModule,
  MatDividerModule,
  MatSidenavModule,
  MatListModule,
  MatAutocompleteModule,
  MatSnackBarModule,
  MatCheckboxModule,
  MatDialogModule,
];

@Pipe({
    name: 'timeAgo',
    pure: false,
    standalone: false
})
export class TimeAgoExtendsPipe extends TimeAgoPipe {
  constructor(changeDetectorRef: ChangeDetectorRef, ngZone: NgZone) {
    super(changeDetectorRef, ngZone);
  }
}

const sharedModules = [
  LayoutSwitcherComponent,
  LocationPickerComponent,
  UserMenuComponent,
  HeaderComponent,
  FullLayoutComponent,
  MinimalLayoutComponent,
  ArrowComponent,
  TimeAgoExtendsPipe,
  AttachmentComponent,
  InsightCardComponent,
  SafeHtmlPipe,
  ConsolidationTrendChartComponent,
  ConsolidationChartComponent,
  CarouselComponent,
  CarouselItemComponent,
  OnboardingOverlayComponent,
  ZoomableImageComponent,
  TimeDurationPipe,
  DialogComponent,
  RemoveAriaOwnsDirective,
];

@NgModule({
  declarations: [
    ...sharedModules,
    FromNowPipe,
    EmailPreviewComponent,
    SafeUrlPipe,
    LocationSidebarComponent,
    CustomerHeaderComponent,
    LocationGuardComponent,
    LocationsModuleComponent,
    UserModuleComponent,
    MobileLocationPickerComponent,
    FeedbackPanelComponent,
    NegativeFeedbackDialogComponent,
    FeedbackDialogComponent,
    ImageSwapperComponent,
    CardTitleComponent,
    DrawerPanelComponent,
    DrawerComponent,
    MultilineTranslationComponent,
    EnablePushPanelComponent,
    QsrLogoPipe,
    ConfirmDialogComponent,
    PosCategorySopDialogComponent,
    HasFeaturePipe,
    HasMatchingPipe,
    HasTimelinessPipe,
    CustomerFacingCategoryLabelPipe,
    VerticalBarChartWithAverageComponent,
  ],
  imports: [
    SharedModule,
    AuthModule,
    ...sharedMatModules,
    NgxChartsModule,
    TranslateModule,
    BarRatingModule,
    MatBadgeModule,
    MatTooltipModule,
  ],
  exports: [
    ...sharedModules,
    ...sharedMatModules,
    FromNowPipe,
    EmailPreviewComponent,
    SafeUrlPipe,
    NgxChartsModule,
    CardTitleComponent,
    TimeDurationPipe,
    DrawerComponent,
    MultilineTranslationComponent,
    QsrLogoPipe,
    HasFeaturePipe,
    HasMatchingPipe,
    HasTimelinessPipe,
    CustomerFacingCategoryLabelPipe,
    VerticalBarChartWithAverageComponent,
  ],
  providers: [DrawerPanelService, CustomCurrencyPipe],
})
export class CustomerSharedModule {}
